import React from 'react';
import { useStudioTypeSize } from '../../StudioTypeScale';
import { resolveComparisonContent } from '../../../../utils/contentCopy';
import { colorWithAlpha } from '../../../../utils/color';
import EditorialFrame, { type EditorialInjectedProps, type EditorialLayoutProps, type EditorialTokens } from './EditorialFrame';

const NEGATIVE_COLOR = '#c63b43';
const POSITIVE_COLOR = '#16865b';

const EditorialColumn: React.FC<{
  title: string;
  items: string[];
  positive: boolean;
  isStory?: boolean;
  tokens: EditorialTokens;
}> = ({ title, items, positive, isStory, tokens: t }) => {
  const color = positive ? POSITIVE_COLOR : NEGATIVE_COLOR;
  const typeSize = useStudioTypeSize();

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        border: `1px solid ${colorWithAlpha(color, 0.34)}`,
        borderRadius: isStory ? 13 : 10,
        background: colorWithAlpha(color, 0.035),
        boxShadow: '0 9px 22px rgba(32, 35, 31, 0.07)'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isStory ? 8 : 6,
          padding: isStory ? '10px 9px' : '8px 7px',
          color: '#ffffff',
          background: color,
          fontSize: typeSize(isStory ? 11 : 9),
          fontWeight: 800,
          letterSpacing: 1.2,
          textTransform: 'uppercase'
        }}
      >
        <span
          style={{
            width: isStory ? 18 : 15,
            height: isStory ? 18 : 15,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.62)',
            borderRadius: '50%',
            fontSize: typeSize(isStory ? 13 : 11),
            lineHeight: 1,
            flexShrink: 0
          }}
        >
          {positive ? '✓' : '×'}
        </span>
        <span>{title}</span>
      </div>
      <div style={{ padding: isStory ? '4px 10px 7px' : '3px 8px 5px' }}>
        {items.map((item, index) => (
          <div
            key={`${index}-${item}`}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: isStory ? 8 : 6,
              minHeight: isStory ? 46 : 38,
              padding: isStory ? '9px 0' : '7px 0',
              borderBottom: index === items.length - 1 ? 'none' : `1px solid ${colorWithAlpha(color, 0.2)}`,
              color: t.ink,
              fontFamily: t.serif,
              fontSize: typeSize(isStory ? 12.5 : 10.5),
              lineHeight: 1.42
            }}
          >
            <span
              style={{
                minWidth: isStory ? 21 : 18,
                height: isStory ? 21 : 18,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                background: colorWithAlpha(color, 0.13),
                color,
                fontFamily: t.sans,
                fontSize: typeSize(isStory ? 9 : 7.5),
                fontWeight: 900,
                lineHeight: 1.1,
                flexShrink: 0
              }}
            >
              {String(index + 1).padStart(2, '0')}
            </span>
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const EditorialComparisonContent: React.FC<EditorialInjectedProps & { content: any }> = ({
  content,
  tokens,
  isStory,
  locale
}) => {
  const t = tokens!;
  const typeSize = useStudioTypeSize();
  const resolved = resolveComparisonContent(content, locale);

  return (
    <div>
      <h2
        style={{
          margin: '0 auto 20px',
          textAlign: 'center',
          color: t.ink,
          fontFamily: t.serif,
          fontSize: typeSize(isStory ? 32 : 25),
          letterSpacing: '-0.8px',
          lineHeight: 1.13
        }}
      >
        {resolved.title}
      </h2>
      <div style={{ display: 'flex', gap: isStory ? 13 : 10, alignItems: 'stretch' }}>
        <EditorialColumn
          title={resolved.leftTitle}
          items={resolved.leftItems}
          positive={false}
          isStory={isStory}
          tokens={t}
        />
        <EditorialColumn
          title={resolved.rightTitle}
          items={resolved.rightItems}
          positive
          isStory={isStory}
          tokens={t}
        />
      </div>
    </div>
  );
};

const EditorialComparisonLayout: React.FC<EditorialLayoutProps> = props => (
  <EditorialFrame {...props}>
    <EditorialComparisonContent content={props.content || {}} />
  </EditorialFrame>
);

export default EditorialComparisonLayout;
